<template lang="pug">
  .rhpa-child(
    :data-cnstrc-item-id="cnstrcChildItemId"
    :data-cnstrc-item-name="cnstrcChildItemId ? product.name : undefined"
    :data-cnstrc-item-variation-id="cnstrcChildItemId ? cnstrcChildVariationId : undefined"
    :data-cnstrc-item-price="cnstrcChildItemId ? cnstrcChildPrice : undefined"
  )
    .simple(:class="{'is-oos':meta.stockStatus !== '', 'accessory': layout === 'accessory'}")
      .top(v-if="!isCustomAccordionProduct || showTop")
        .thumb(v-if="layout === 'accessory'" @click="uiStore.showOverlay(`accessory-details-${product.sku}`, $rogue)")
          StaticImage(:src="product.thumbnail || product.image" :width="64" alt="")
        .simple-name
          span {{ displayName || product.name }}
          a.details-link(v-if="layout === 'accessory'" href="" @click.prevent="uiStore.showOverlay(`accessory-details-${product.sku}`, $rogue)") {{ __('See Details')}}
          Price.simple-price(
            v-if="layout === 'accessory'"
            :buyRequest="buyRequest"
            :showTotalPrice="false"
            :showExclVat="false"
            :theme="'rhpa-child condensed accessories'"
          )
        Price.simple-price(
          v-if="layout !== 'accessory' && showPrice"
          :buyRequest="buyRequest"
          :showTotalPrice="false"
          :showExclVat="false"
          :theme="'rhpa-child' + (['AUS','EUR','CAN','GBR'].includes($rogue.site.code) ? ' condensed' : '')"
        )
        BuyRequestQty(
          v-if="showQty && meta.stockStatus !== 'bin' && meta.stockStatus !== 'oos'"
          :buyRequest="buyRequest"
          :status="meta.stockStatus"
          :theme="layout === 'accessory' ? 'accessory' : 'group-child'"
        )
      RogueButton.bin-signup.notify-me(v-if="meta.stockStatus === 'bin'" :loading="BIN.loading" @click="clickBinButton($el)" )
        bin-status-text(:bin="BIN")
      RogueButton.notify-me(
        v-if="meta.stockStatus === 'oos'"
        color="flat-disabled"
        :width="$mq === 'mobile' ? 'full' : 'normal'"
      )
        span.unavailable-text(:class="$rogue.l10n.lang !== 'en' ? 'allow-wrap' : undefined" v-if="layout === 'accessory'")
          | {{__('Unavailable')}}
        span.unavailable-text(:class="$rogue.l10n.lang !== 'en' ? 'allow-wrap' : undefined" v-else)
          | {{__('Item is Unavailable')}}
    product-badges(v-if="shouldDisplayBadges" :class="{ accessory: layout === 'accessory' }" :meta="meta" theme="group-child" :parentMeta="groupMeta")
    .rhpa-stock(v-if="shortDescDisplay && layout !== 'accessory'" v-html="__(shortDescDisplay, 'auto')" )
    bin-email-input(:bin="BIN" @signup="clickBinButton($el)")
    Drawer.rhpa-details-drawer(v-if="overlay === `accessory-details-${product.sku}`" :title="__(product.name)" @hide="uiStore.hideOverlay()")
      AccessorySummary(v-if="detailType === 'accessory'" :productId="product.productId")
      template(#footer)
        RogueButton(v-if="detailType === 'accessory'" @click="uiStore.hideOverlay()" width="full") {{ __('Return to Product Page') }}
      ProductSummary(v-if="detailType !== 'accessory'" :productId="product.productId")
    .rogue-hide(v-if="$rogue.ssrContext?.queryParams?.SAVE_TRANSLATIONS")
      AccessorySummary(v-if="detailType === 'accessory'" :productId="product.productId")
      ProductSummary(v-else :productId="product.productId")
</template>
<script>
import { defineAsyncComponent } from 'vue'
import { computePriceSync } from '@/js/helpers/price-utils'
import { getBuyRequestMeta } from '../../helpers/buy-request'
import ProductBadges from './badges.vue'
import ProductBadge from './badge.vue'
import { useProductStore } from '@/js/stores/productStore'
import { useUIStore } from '@/js/stores/uiStore'
import { useBackInStock } from '@/js/components/bin/useBackInStock'
import BuyRequestQty from '@/js/components/buy-request-qty.vue'
import BinEmailInput from '@/js/components/bin/bin-email-input.vue'
import BinStatusText from '@/js/components/bin/bin-status-text.vue'
import { isEnabledCustomProduct } from '@/js/customProducts/registry'
import { buildBundleVariationId } from '@/js/services/cnstrc/util'

export default {
  name       : 'RhpaChild',
  components : {
    ProductBadge,
    ProductBadges,
    BuyRequestQty,
    BinEmailInput,
    BinStatusText,
    ProductSummary   : defineAsyncComponent(() => import('@/js/pages/product/product-summary.vue')),
    AccessorySummary : defineAsyncComponent(() => import('@/js/pages/product/accessory-summary.vue')),
  },
  props: {
    buyRequest: {
      type     : Object,
      required : true,
    },
    layout: {
      type     : String,
      required : true,
    },
    groupMeta: {
      type     : Object,
      required : true,
    },
    // Used for bumper set breakdown condensed product names in drawer and editing
    displayName: {
      type     : String,
      required : false,
    },
    showBadges: {
      type    : Boolean,
      default : true,
    },
    // Cave 2.0 & SlingerX hides this row inside each accordion. 
    showTop: {
      type    : Boolean,
      default : false,
    },
    // Cave 2.0 & SlingerX Set to false to suppress the per-item price
    showPrice: {
      type    : Boolean,
      default : true,
    },
    // Cave 2.0 & SlingerX set to false to suppress the qty stepper. 
    showQty: {
      type    : Boolean,
      default : true,
    },
  },

  setup(props) {
    const productStore = useProductStore()
    const uiStore = useUIStore()
    const { BIN, clickBinButton } = useBackInStock(props.buyRequest)

    return {
      productStore,
      uiStore,
      BIN,
      clickBinButton,
    }
  },

  data: () => ({
    forceMobile : false,
    binStatus   : null,
    binEmail    : '',
    detailType  : 'accessory',
    meta        : {},
  }),

  computed: {
    overlay() {
      return this.uiStore.overlay
    },

    isCustomAccordionProduct() {
      const rack = this.$rogue.getProductById(this.buyRequest?.productId)

      return isEnabledCustomProduct(this.$rogue, 'cave', rack) || isEnabledCustomProduct(this.$rogue, 'slinger-x', rack)
    },

    shouldDisplayBadges() {
      return this.isCustomAccordionProduct ? false : this.showBadges // the badge should not show under the accordion for the cave
    },

    products() {
      return this.productStore.products
    },

    product() {
      return this.products[this.buyRequest.productId] || { inventory: { max: 0 } }
    },

    simple() {
      return this.products[this.meta.id] || { inventory: { min: 0, max: 0 } }
    },

    threeFree() {
      return (this.shortDesc || '').indexOf('3 Ships Free') !== -1
    },

    customStock() {
      return this.product?.attributes?.custom_stock_status || this.simple?.attributes?.custom_stock_status
    },

    shortDesc() {
      return this.product?.attributes?.short_description || this.simple?.attributes?.short_description
    },

    shortDescDisplay() {
      if (this.threeFree) {
        return this.shortDesc.replace(/3 Ships Free\.?/, '').trim()
      }

      return this.shortDesc
    },

    cnstrcChildPrice() {
      try {
        return computePriceSync({ ...this.buyRequest, qty: 1 }, this.$rogue.pinia) || undefined
      } catch {
        return undefined
      }
    },

    // Constructor item-id for grouped child rows.
    cnstrcChildItemId() {
      if (this.buyRequest.qty <= 0) return undefined

      // Bundle child of a grouped page (e.g. FM-6 Twin under the FM-6 page):
      // item-id = grouped parent product ID — matches the parent .rhpa container
      // so Constructor sees the same item on both elements (no mismatch)
      if (this.product.type === 'bundle' || this.layout !== 'accessory') {
        return this.buyRequest.metadata?.groupedProductId
      }

      return this.product.productId
    },

    cnstrcChildVariationId() {
      // Bundle child of grouped page: build the full variation string so it matches
      // the parent .rhpa container. colorSimple is at children[0].children[0]
      if (this.product.type === 'bundle') {
        const colorSimpleId = this.buyRequest.children?.[0]?.children?.[0]?.productId

        if (!colorSimpleId) return undefined

        const colorSimple = this.products[colorSimpleId]

        if (!colorSimple) return undefined

        return buildBundleVariationId(
          colorSimple,
          this.buyRequest,
          this.product,
          this.$route?.query?.b,
          this.products,
          this.$rogue
        )
      }

      const childId = this.buyRequest.children?.[0]?.productId

      return childId !== undefined && childId !== this.product.productId ? childId : undefined
    },
  },

  watch: {
    buyRequest: {
      deep      : true,
      immediate : true,
      handler() {
        this.meta = getBuyRequestMeta(this.buyRequest, this.$rogue)
      },
    },
    meta: {
      deep: true,
      handler(newMeta, oldMeta) {
        const newStock = newMeta?.stockStatus
        const oldStock = oldMeta?.stockStatus

        // Fail out if there are no changes in stock status or if there is a PDP change
        if (newStock === oldStock || newMeta?.groupSku !== oldMeta?.groupSku) return

        if (newStock !== '' && this.buyRequest.qty > 0) {
          this.buyRequest.qty = 0
        } else if (newStock === '' && this.buyRequest.qty === 0 && oldStock && oldStock !== '') {
          this.buyRequest.qty = 1
        }
      },
    },
  },

  mounted() {
    this.forceMobile = this.$el.closest('.rhpa')?.classList.contains('force-mobile')
  },
}
</script>
<style lang="scss">
.rhpa:not(.force-mobile) {
  .rhpa-child {
    width: 100%;
    @include not-mobile() {
      .simple.is-oos {
        flex-wrap: nowrap;

        .qty.group-child {
          display: none;
        }

        .button {
          white-space: nowrap;
          max-width: 164px;
        }
      }
      .simple-qty {
        min-width: 164px;
        max-width: 164px;
      }
      .bin-signup {
        margin-top: 0;
        flex-shrink: 0;
        flex-grow: 0;
        width: 164px
      }
    }
  }
}

.rhpa-child .short-desc {
  li {
    font-size: $size-xxs;
  }
}

.rhpa-child .accessory .badges {
  margin-top: $space-xs;
}

.rhpa-child .ttip .tip {
  span, p, li, br {
    line-height: $line-height-m;
  }

  // Make sure they wont add extra margin/padding
  span:empty, p:empty, li:empty {
    display: none;
  }
}

.rhpa-details-drawer {
  .drawer-body {
    padding: $space-s;
  }

  .accessory-summary-section {
    padding: $space-s;
  }

  .lead-time {
    display: flex;
    align-items: center;

    .icon {
      display: inline-block;
      margin-right: $space-xxs;
      width: 24px;
    }
  }

  .divider {
    height: 1px;
    background-color: $color-gray;
    margin: $size-xs 0 $size-xs;
  }

  .drawer-footer {
    background-color: $color-white;
    padding: $space-xs;
    box-shadow: 0 -5px 5px -2.5px rgba($color-black, 0.1);
  }
}
</style>
<style lang="scss" scoped>
.simple {
  display: flex;
  align-items: center;
  width: 100%;
  .top {
    display: flex;
    align-items: center;
    width: 100%;
  }

  &.is-oos {
    flex-wrap: wrap;
  }

  &.accessory {
    .thumb {
      width: 64px;
      margin-right: $space-s;
      flex-shrink: 0;
    }

    .details-link {
      display: block;
      margin-top: $space-xxxs;
      color: $color-gray-x-dark;
    }

    .notify-me {
      width: 120px;
    }
  }
}

.simple-name {
  font-size: $size-xs;
  flex-grow: 1;
  padding-right: $space-xxs;
  word-break: break-word;
}

.disabled {
  opacity: $opacity-disabled;
  pointer-events: none;
}

.simple-qty {
  min-width: 60px;
  max-width: inherit;
}

.bin-signup {
  margin-top: $space-xs;
  width: 100%;
}

.rhpa-stock, .short-desc {
  margin-top: $space-s;
  font-size: $size-xxs;
}

@include mobile {
  .notify-me {
    margin-top: $space-xs;
  }
}

.unavailable-text.allow-wrap {
  white-space: normal;
}
</style>
